import { CheckedAddress, raise } from '@sparkdotfi/common-universal'
import { notFound, useParams } from '@tanstack/react-router'
import { useChainId } from 'wagmi'

import { InterestRateModelPanelProps } from '../components/interest-rate-model-panel/InterestRateModelPanel'
import { useCapAutomatorInfo } from '../data/cap-automator/useCapAutomatorInfo'
import { MarketOverview, WalletOverview } from '../types'
import { makeInterestRateModelInfo } from './makeInterestRateModelInfo'
import { makeMarketOverview } from './makeMarketOverview'
import { makeWalletOverview } from './makeWalletOverview'
import { useBorrowRateHistoric } from './useBorrowRateHistoric'
import { useSparkRewards } from './useSparkRewards'
import { useSupplyRateHistoric } from './useSupplyRateHistoric'
import { useUsdcViaUsdsProxyMarket } from './useUsdcViaUsdsProxyMarket'
import { getChainConfigEntry } from '@/config/chain'
import { getNativeAssetInfo } from '@/config/chain/utils/getNativeAssetInfo'
import { useSandbox } from '@/domain/sandbox/useSandbox'
import { useSandboxPageRedirect } from '@/domain/sandbox/useSandboxPageRedirect'
import { useOpenDialog } from '@/domain/state/dialogs/hooks'
import { useMarketInfo } from '@/features/sparklend/common/data/market-info/useMarketInfo'
import { UseOracleInfoResult, useOracleInfo } from '@/features/sparklend/common/data/oracles/useOracleInfo'
import { useMarketWalletInfo } from '@/features/sparklend/common/data/useMarketWalletInfo'
import { SparkLendToken } from '@/features/sparklend/tokens/SparkLendToken'
import { InternalLink } from '@/routing/types'

export interface UseMarketDetailsResult {
  token: SparkLendToken
  aToken: SparkLendToken
  variableDebtTokenAddress: CheckedAddress
  chainName: string
  chainId: number
  marketOverview: MarketOverview
  walletOverview: WalletOverview
  chainMismatch: boolean
  oracleInfo: UseOracleInfoResult
  interestRateModel?: InterestRateModelPanelProps
  sllManaged: boolean
  swapAvailable: boolean
  usdcViaUsdsProxyMarket?: {
    link: InternalLink
    selected: boolean
    showBorrowFromProxyMarketBanner: boolean
  }
  openSandboxModal: () => void
}

export function useMarketDetails(): UseMarketDetailsResult {
  const { chainId, asset } = useParams({ from: '/markets/$chainId/$asset' })

  const { isInSandbox, openSandboxModal } = useSandbox()
  const { marketInfo } = useMarketInfo({ chainId })
  const walletInfo = useMarketWalletInfo({ chainId })
  const { meta: chainMeta, savings } = getChainConfigEntry(chainId)
  const connectedChainId = useChainId()

  const openDialog = useOpenDialog()

  useSandboxPageRedirect({
    redirect: InternalLink({ to: '/markets/$chainId/$asset', params: { chainId, asset } }),
    fallback: InternalLink({ to: '/markets' }),
  })
  const nativeAssetInfo = getNativeAssetInfo(marketInfo.chainId)

  const chainMismatch = connectedChainId !== chainId

  const reserve = marketInfo.findReserveByUnderlyingAsset(asset) ?? raise(notFound())

  const { capAutomatorInfo } = useCapAutomatorInfo({
    chainId,
    token: reserve.token,
  })

  const oracleInfo = useOracleInfo({
    reserve,
    marketInfo,
  })

  const sparkRewards = useSparkRewards({
    chainId,
    reserve,
  })
  const supplyRateHistoricResult = useSupplyRateHistoric({ chainId, symbol: reserve.token.symbol })
  const borrowRateHistoricResult = useBorrowRateHistoric({ chainId, symbol: reserve.token.symbol })

  const usdcViaUsdsProxyMarket = useUsdcViaUsdsProxyMarket({ marketInfo, reserve, isInSandbox })

  const marketOverview = makeMarketOverview({
    reserve,
    marketInfo,
    capAutomatorInfo,
    sparkRewards,
    supplyRateHistoricResult,
    borrowRateHistoricResult,
  })

  const walletOverview = makeWalletOverview({
    reserve,
    marketInfo,
    walletInfo,
    connectedChainId,
    nativeAssetInfo,
    openDialog,
    usdcViaUsdsProxyMarket,
  })

  const interestRateModel =
    reserve.borrowEligibilityStatus === 'no' ? undefined : makeInterestRateModelInfo({ chainId, reserve })

  const swapAvailable = !!savings?.psmStables?.includes(reserve.token.symbol)

  return {
    token: reserve.token,
    aToken: reserve.aToken,
    variableDebtTokenAddress: reserve.variableDebtTokenAddress,
    chainName: chainMeta.name,
    chainId,
    marketOverview,
    walletOverview,
    chainMismatch,
    oracleInfo,
    interestRateModel,
    openSandboxModal,
    sllManaged: reserve.sllManaged,
    swapAvailable,
    usdcViaUsdsProxyMarket,
  }
}
